// The day's route as the editor gets it from the page: [lng, lat, distance_m]
// positions in walking order. A checkpoint is a distance along this line, so a
// point dragged on the map is snapped back onto the line and stored as the
// distance it landed at.

export type RoutePosition = [number, number, number];

const EARTH_RADIUS_M = 6_371_008.8;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Local equirectangular metres east/north of an origin — plenty for snapping a drag. */
function project(originLat: number, lng: number, lat: number): [number, number] {
  const metresPerDegree = (Math.PI / 180) * EARTH_RADIUS_M;
  return [lng * metresPerDegree * Math.cos(toRadians(originLat)), lat * metresPerDegree];
}

/** [lng, lat] at a distance along the route, interpolated between positions. */
export function pointAt(route: RoutePosition[], distance: number): [number, number] {
  if (route.length === 0) return [0, 0];
  if (distance <= route[0][2]) return [route[0][0], route[0][1]];
  const last = route[route.length - 1];
  if (distance >= last[2]) return [last[0], last[1]];

  let low = 0;
  let high = route.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (route[mid][2] <= distance) low = mid; else high = mid;
  }
  const a = route[low];
  const b = route[high];
  const span = b[2] - a[2];
  const t = span > 0 ? (distance - a[2]) / span : 0;
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Distance along the route of the nearest point on it to [lng, lat] — the
 * foot of the perpendicular on the closest segment, not just the closest
 * vertex, so a point can sit anywhere along a long straight.
 *
 * `within` limits the search to a stretch of the route, so a point can't be
 * dragged past its neighbours and jump onto a part of a loop that passes close by.
 */
export function snapToRoute(
  route: RoutePosition[],
  lng: number,
  lat: number,
  within: [number, number] = [-Infinity, Infinity],
): number {
  if (route.length === 0) return 0;
  if (route.length === 1) return route[0][2];

  const [minD, maxD] = within;
  const [px, py] = project(lat, lng, lat);
  let best = { distanceSq: Infinity, along: Math.min(Math.max(route[0][2], minD), maxD) };

  for (let i = 0; i < route.length - 1; i += 1) {
    const a = route[i];
    const b = route[i + 1];
    if (b[2] < minD || a[2] > maxD) continue;

    const [ax, ay] = project(lat, a[0], a[1]);
    const [bx, by] = project(lat, b[0], b[1]);
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / lengthSq)) : 0;
    const along = Math.min(Math.max(a[2] + (b[2] - a[2]) * t, minD), maxD);
    const [sx, sy] = project(lat, ...pointAt(route, along));
    const distanceSq = (px - sx) ** 2 + (py - sy) ** 2;
    if (distanceSq < best.distanceSq) best = { distanceSq, along };
  }
  return best.along;
}

/** Where a section's "0–1" label sits: half way along it. */
export function sectionLabelAt(route: RoutePosition[], from: number, to: number): [number, number] {
  return pointAt(route, (from + to) / 2);
}

/** [[west, south], [east, north]] of the route, for framing the map. */
export function routeBounds(route: RoutePosition[]): [[number, number], [number, number]] | null {
  if (route.length === 0) return null;
  let west = Infinity; let south = Infinity; let east = -Infinity; let north = -Infinity;
  route.forEach(([lng, lat]) => {
    west = Math.min(west, lng); east = Math.max(east, lng);
    south = Math.min(south, lat); north = Math.max(north, lat);
  });
  return [[west, south], [east, north]];
}
